import { withTranslation } from 'react-i18next';
import moment from 'moment';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { ACTION_COLUMNS } from '../keys';
import { actionRows } from '../rows';

/**
 * Block 04 — the refresh, the export, and the things this page deliberately does not do.
 *
 * Refresh is a button because it has to be. An hourly view invites a polling loop, and a polling
 * loop is a monitor: it would spend the property's request budget unattended, and it would turn a
 * delayed, revisable feed into something a reader treats as an alert. This page fetches when
 * asked, once, and says so where someone would otherwise go looking for the auto-refresh setting.
 *
 * A failed refresh leaves the previous result on screen, labelled with the time it was fetched
 * and the reason the newer attempt failed. Blanking the page would lose figures that are still
 * perfectly good; replacing them silently would be worse.
 *
 * Both actions are the table's own controls, in the app's standard export group in the table
 * header, rather than a pair of page buttons above it. The export is deliberately not this
 * table's rows: the file worth having is the window's hours with their offsets, completeness and
 * comparison coverage, which is what the block is named for.
 */
function RefreshExport({ t, scope, exportable, loading, error, fetchedAt, onRefresh, onExport }) {
    const columns = ACTION_COLUMNS(t);
    const rows = actionRows(t, { scope, exportable });

    /**
     * Refresh and export as one `.dt-exports` group, the way the shared Exports component pairs
     * CSV with PDF.
     *
     * The refresh is drawn here rather than handed to DataTable's `forceRun`, which would put it
     * in the header and leave the export in the actions bar below — two buttons on two rows, a
     * step apart diagonally. These belong together: they are the two halves of what this block
     * is for. Same classes and same icon as the one `forceRun` draws, so it is the app's refresh
     * button in all but which prop supplied it.
     */
    const actionButtons = (
        <div className="dt-exports">
            <button
                className="dt-exports__button"
                type="button"
                onClick={onExport}
                disabled={!exportable}
            >
                <i className="icon-fi_download" /> {t('table.csv')}
            </button>
            <button
                className="dt-exports__button with-refresh"
                type="button"
                title={t('tools.latest_hours.actions.refresh')}
                aria-label={t('tools.latest_hours.actions.refresh')}
                onClick={onRefresh}
            >
                <i className="icon-Frame-converted" />
            </button>
        </div>
    );

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.latest_hours.actions.title')}</p>
                    <p className="gsc-tool-sub">{t('tools.latest_hours.actions.description')}</p>
                </div>
            </div>

            <div className="card-body">
                {/* The refresh icon is in the table header below, where every table in the app
                    puts it, so the fact that one is in flight has to be said rather than shown by
                    a button changing state. */}
                {loading && (
                    <div className="gsc-tool-banner">
                        {t('tools.latest_hours.actions.refreshing')}
                    </div>
                )}

                {/* The refresh failed but the figures above are the last good ones. Both facts
                    are stated together: the error, and what the page is still showing. */}
                {!loading && !!error && (
                    <div className="gsc-tool-banner error">
                        {exportable
                            ? t('tools.latest_hours.actions.refresh_failed_kept', {
                                  reason: error,
                                  time: fetchedAt
                                      ? moment(fetchedAt).format('DD MMM YYYY, HH:mm')
                                      : '',
                              })
                            : t('tools.latest_hours.actions.refresh_failed', { reason: error })}
                    </div>
                )}

                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    // The header row, which is where the group belongs and where it is already
                    // right-aligned. A press while a fetch is in flight is harmless: the second
                    // supersedes the first through the page's generation counter.
                    extraHeaders={actionButtons}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">{t('tools.latest_hours.actions.no_storage')}</p>
                <p className="gsc-tool-table-note">{t('tools.latest_hours.actions.export_note')}</p>
            </div>
        </div>
    );
}

export default withTranslation()(RefreshExport);
